/**
 * ConfirmModal & Toast — Production replacements for window.confirm and window.alert
 */
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { AlertCircle, CheckCircle2, X, AlertTriangle, Info } from 'lucide-react';


// ====================================================================
// ConfirmModal — replaces window.confirm
// ====================================================================

export const ConfirmModal = ({
  isOpen,
  title = 'Confirm',
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default', // 'default' | 'danger' | 'warning'
  onConfirm,
  onCancel,
}) => {
  if (!isOpen) return null;

  const variantStyles = {
    default: {
      icon: <Info className="h-6 w-6 text-blue-500" />,
      confirmBtn: 'bg-blue-600 hover:bg-blue-700 text-white',
    },
    danger: {
      icon: <AlertCircle className="h-6 w-6 text-red-500" />,
      confirmBtn: 'bg-red-600 hover:bg-red-700 text-white',
    },
    warning: {
      icon: <AlertTriangle className="h-6 w-6 text-yellow-500" />,
      confirmBtn: 'bg-yellow-600 hover:bg-yellow-700 text-white',
    },
  };

  const styles = variantStyles[variant] || variantStyles.default;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 overflow-hidden">
        <div className="p-6">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0 mt-0.5">{styles.icon}</div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
              <p className="mt-2 text-sm text-gray-600">{message}</p>
            </div>
          </div>
        </div>
        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${styles.confirmBtn}`}
          >
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
};


// ====================================================================
// ValidationModal — shows errors/warnings before save
// ====================================================================

export const ValidationModal = ({
  isOpen,
  errors = [],
  warnings = [],
  onFix,      // "Go back and fix"
  onSaveAnyway, // Save despite warnings (only if no errors)
  onClose,
}) => {
  if (!isOpen) return null;
  const hasErrors = errors.length > 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[100]">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 overflow-hidden">
        <div className="p-6">
          <div className="flex items-center space-x-3 mb-4">
            {hasErrors
              ? <AlertCircle className="h-6 w-6 text-red-500" />
              : <AlertTriangle className="h-6 w-6 text-yellow-500" />
            }
            <h3 className="text-lg font-semibold text-gray-900">
              {hasErrors ? 'Workflow Has Errors' : 'Workflow Warnings'}
            </h3>
          </div>

          {errors.length > 0 && (
            <div className="mb-4">
              <h4 className="text-sm font-medium text-red-700 mb-2">Errors (must fix)</h4>
              <ul className="space-y-1">
                {errors.map((err, i) => (
                  <li key={i} className="text-sm text-red-600 flex items-start space-x-2">
                    <span className="text-red-400 mt-0.5">✕</span>
                    <span>{err}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {warnings.length > 0 && (
            <div>
              <h4 className="text-sm font-medium text-yellow-700 mb-2">Warnings</h4>
              <ul className="space-y-1">
                {warnings.map((warn, i) => (
                  <li key={i} className="text-sm text-yellow-600 flex items-start space-x-2">
                    <span className="text-yellow-400 mt-0.5">⚠</span>
                    <span>{warn}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="bg-gray-50 px-6 py-4 flex justify-end space-x-3">
          <button
            onClick={onFix || onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
          >
            Go Back & Fix
          </button>
          {!hasErrors && onSaveAnyway && (
            <button
              onClick={onSaveAnyway}
              className="px-4 py-2 text-sm font-medium text-white bg-yellow-600 rounded-md hover:bg-yellow-700"
            >
              Save Anyway
            </button>
          )}
        </div>
      </div>
    </div>
  );
};


// ====================================================================
// Toast — replaces window.alert for non-blocking notifications
// ====================================================================

export const Toast = ({ message, type = 'info', isVisible, onClose, duration = 4000 }) => {
  useEffect(() => {
    if (isVisible && duration > 0) {
      const timer = setTimeout(onClose, duration);
      return () => clearTimeout(timer);
    }
  }, [isVisible, duration, onClose]);

  if (!isVisible) return null;

  const typeStyles = {
    success: 'bg-green-50 border-green-200 text-green-800',
    error: 'bg-red-50 border-red-200 text-red-800',
    warning: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    info: 'bg-blue-50 border-blue-200 text-blue-800',
  };

  const icons = {
    success: <CheckCircle2 className="h-5 w-5 text-green-500" />,
    error: <AlertCircle className="h-5 w-5 text-red-500" />,
    warning: <AlertTriangle className="h-5 w-5 text-yellow-500" />,
    info: <Info className="h-5 w-5 text-blue-500" />,
  };

  return (
    <div className="fixed top-4 right-4 z-[200] animate-slide-in">
      <div className={`flex items-center space-x-3 px-4 py-3 rounded-lg border shadow-lg ${typeStyles[type]}`}>
        {icons[type]}
        <span className="text-sm font-medium">{message}</span>
        <button onClick={onClose} className="ml-2 opacity-60 hover:opacity-100">
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};


// ====================================================================
// useConfirm — Hook for imperative confirmation dialogs
// ====================================================================

/**
 * Usage:
 *   const { confirm, ConfirmDialog } = useConfirm();
 *   
 *   const handleDelete = async () => {
 *     const ok = await confirm({
 *       title: 'Delete Workflow',
 *       message: 'This cannot be undone.',
 *       variant: 'danger',
 *       confirmText: 'Delete'
 *     });
 *     if (ok) { ... }
 *   };
 * 
 *   return <>{...}<ConfirmDialog /></>;
 */
export function useConfirm() {
  const [state, setState] = useState({ isOpen: false, props: {} });
  const resolveRef = useRef(null);

  const confirm = useCallback((props) => {
    return new Promise((resolve) => {
      resolveRef.current = resolve;
      setState({ isOpen: true, props });
    });
  }, []);

  const handleConfirm = useCallback(() => {
    setState({ isOpen: false, props: {} });
    resolveRef.current?.(true);
  }, []);

  const handleCancel = useCallback(() => {
    setState({ isOpen: false, props: {} });
    resolveRef.current?.(false);
  }, []);

  const ConfirmDialog = () => (
    <ConfirmModal
      isOpen={state.isOpen}
      {...state.props}
      onConfirm={handleConfirm}
      onCancel={handleCancel}
    />
  );

  return { confirm, ConfirmDialog };
}


// ====================================================================
// useToast — Hook for imperative toast notifications
// ====================================================================

/**
 * Usage:
 *   const { showToast, ToastContainer } = useToast();
 *   showToast('Saved!', 'success');
 *   return <>{...}<ToastContainer /></>;
 */
export function useToast() {
  const [toast, setToast] = useState({ isVisible: false, message: '', type: 'info' });

  const showToast = useCallback((message, type = 'info', duration = 4000) => {
    setToast({ isVisible: true, message, type, duration });
  }, []);

  const hideToast = useCallback(() => {
    setToast(prev => ({ ...prev, isVisible: false }));
  }, []);

  const ToastContainer = () => (
    <Toast
      message={toast.message}
      type={toast.type}
      isVisible={toast.isVisible}
      onClose={hideToast}
      duration={toast.duration}
    />
  );

  return { showToast, ToastContainer };
}
